import { CURRENCIES, type Currency } from '@fos/shared/contracts/common';
import type {
  CompanyLookupOutput,
  CompanyRegistrySource,
  CompanyVatStatus,
} from '@fos/shared/contracts/company-registry';
import {
  CLIENT_EDITABLE_STATUSES,
  type ClientCompanyInput,
  type ClientDto,
  type ClientEditableStatus,
  type ClientLink,
} from '@fos/shared/contracts/crm';
import { CLIENT_STATUS_LABELS, COMPANY_VAT_STATUS_LABELS, COMPANY_VAT_STATUS_TONES } from '@fos/shared/labels';
import { isValidRegon, normalizeNip, taxIdSchema } from '@fos/shared/nip';
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Select, type SelectOption } from '../../../ui/Select';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { normalizeUrlInput } from '../client-format';
import { ClientColorField, isValidHex, normalizeHex } from './ClientColorField';
import { NipLookupField, registrySourceLabel } from './CompanyLookup';

const NO_OWNER = '__none__';
const RATE_RE = /^\d{1,8}([.,]\d{1,2})?$/;

interface LinkRow {
  key: number;
  label: string;
  url: string;
}

interface FormState {
  name: string;
  status: ClientEditableStatus;
  defaultRate: string;
  currency: Currency;
  color: string | null;
  ownerUserId: string | null;
  nip: string;
  legalName: string;
  regon: string;
  krs: string;
  vatStatus: CompanyVatStatus | null;
  street: string;
  buildingNo: string;
  apartmentNo: string;
  postalCode: string;
  city: string;
  country: string;
  website: string;
  /** dane z rejestru (Fala 7): źródło, data i NIP, dla którego je pobrano — serwer potwierdza je pamięcią podręczną */
  registrySource: CompanyRegistrySource | null;
  registryCheckedAt: string | null;
  registryNip: string | null;
  notes: string;
  links: LinkRow[];
}

type Errors = Record<string, string>;

let linkSeq = 0;
const toRows = (links: ClientLink[]): LinkRow[] => links.map((l) => ({ key: ++linkSeq, ...l }));

function initialState(client: ClientDto | null | undefined, currentUserId: string): FormState {
  const company = client?.company ?? {};
  const address = company.address;
  return {
    name: client?.name ?? '',
    status: client && client.status !== 'archived' ? client.status : 'active',
    defaultRate: client?.defaultRate ? String(Number(client.defaultRate)) : '',
    currency: ((client?.currency as Currency | undefined) ?? 'PLN') as Currency,
    color: client?.color ?? null,
    ownerUserId: client ? client.ownerUserId : currentUserId,
    nip: company.nip ?? '',
    legalName: company.legalName ?? '',
    regon: company.regon ?? '',
    krs: company.krs ?? '',
    vatStatus: company.vatStatus ?? null,
    street: address?.street ?? '',
    buildingNo: address?.buildingNo ?? '',
    apartmentNo: address?.apartmentNo ?? '',
    postalCode: address?.postalCode ?? '',
    city: address?.city ?? '',
    country: address?.country ?? 'PL',
    website: company.website ?? '',
    registrySource: company.registrySource ?? null,
    registryCheckedAt: company.registryCheckedAt ?? null,
    registryNip: company.registrySource ? (company.nip ?? null) : null,
    notes: client?.notes ?? '',
    links: toRows(client?.links ?? []),
  };
}

/** Cyfry i litery numeru podatkowego bez odstępów, myślników i prefiksu „PL” (zmiana zapisu ≠ zmiana NIP). */
function taxIdDigits(value: string): string {
  return value
    .replace(/[\s .\-–]/g, '')
    .toUpperCase()
    .replace(/^PL/, '');
}

/** Pola sekcji „Dane firmy” (błąd w którymkolwiek rozwija sekcję). */
const COMPANY_KEYS = [
  'nip',
  'legalName',
  'regon',
  'krs',
  'street',
  'buildingNo',
  'apartmentNo',
  'postalCode',
  'city',
  'country',
  'website',
] as const;

/** Czy sekcja „Dane firmy” ma treść (kraj „PL” to wartość domyślna). */
function hasCompanyData(f: FormState): boolean {
  return COMPANY_KEYS.some((k) => {
    const v = f[k].trim();
    return v !== '' && !(k === 'country' && v === 'PL');
  });
}

/** Ścieżka z serwera (`company.website`, `company.address.city`, `links.0.url`) → klucz pola formularza. */
function fieldKey(path: string): string {
  if (path.startsWith('company.address.')) return path.slice('company.address.'.length);
  if (path === 'company.address') return 'street';
  if (path.startsWith('company.')) return path.slice('company.'.length);
  const link = /^links\.(\d+)\.(label|url)$/.exec(path);
  if (link) return `link.${link[1]}.${link[2]}`;
  return path || 'form';
}

/**
 * Modal „Nowy klient” / edycja danych klienta (DESIGN §4: 560 px, Ghost „Anuluj” + Primary). Pola: nazwa, status,
 * stawka i waluta (tylko z flagą „Widzi stawki i kwoty”), kolor, opiekun, dane firmy (opcjonalnie), notatki, linki.
 */
export function ClientFormDialog({
  open,
  onOpenChange,
  client,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** edycja istniejącego klienta; brak = nowy klient */
  client?: ClientDto | null;
  onSaved?: (client: ClientDto) => void;
}) {
  const session = useActiveSession();
  const access = useAccess();
  const seesRates = access.flag('seesRates');
  const editing = Boolean(client);
  const utils = trpc.useUtils();
  const members = trpc.team.listMembers.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const [form, setForm] = useState<FormState>(() => initialState(client, session.user.id));
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [showCompany, setShowCompany] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // Otwarcie modala = świeży formularz z aktualnych danych.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    const next = initialState(client, session.user.id);
    setForm(next);
    setErrors({});
    setFormError(null);
    setShowCompany(hasCompanyData(next));
  }, [open]);

  /**
   * NIP zmieniony po „Użyj danych” = dane z rejestru nie dotyczą już tego numeru (pola edytowalne zostają do poprawienia).
   * Status VAT (bez pola w formularzu, tylko z rejestru) znika przy każdej zmianie cyfr NIP — inaczej status poprzedniej
   * firmy trafiłby do zapisu bez możliwości poprawienia; wraca po „Użyj danych” dla nowego NIP.
   */
  function setNip(value: string) {
    setForm((f) => {
      const next =
        taxIdDigits(value) !== taxIdDigits(f.nip) ? { ...f, nip: value, vatStatus: null } : { ...f, nip: value };
      const changed = f.registryNip !== null && normalizeNip(value) !== f.registryNip;
      return changed ? { ...next, registrySource: null, registryCheckedAt: null, registryNip: null } : next;
    });
    setErrors(({ nip: _nip, ...rest }) => rest);
  }

  /** „Użyj danych”: pola firmy z rejestru (wszystko dalej edytowalne); nazwa wyświetlana, gdy jeszcze pusta. */
  function applyRegistryResult(result: CompanyLookupOutput) {
    const c = result.company;
    if (!c) return;
    setForm((f) => ({
      ...f,
      name: f.name.trim() ? f.name : (result.suggestedName ?? c.legalName),
      legalName: c.legalName,
      regon: c.regon ?? '',
      krs: c.krs ?? '',
      // „nieznany” (REGON nie podaje statusu VAT) = brak danych: znany status tego NIP zostaje
      vatStatus: c.vatStatus === 'unknown' ? f.vatStatus : c.vatStatus,
      street: c.address.street,
      buildingNo: c.address.buildingNo,
      apartmentNo: c.address.apartmentNo,
      postalCode: c.address.postalCode,
      city: c.address.city,
      country: c.address.country || 'PL',
      registrySource: result.source,
      registryCheckedAt: new Date(result.fetchedAt).toISOString(),
      registryNip: result.nip,
    }));
    setErrors({});
    setShowCompany(true);
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      if (!e[key as string]) return e;
      const { [key as string]: _, ...rest } = e;
      return rest;
    });
  };

  const ownerOptions = useMemo<SelectOption[]>(() => {
    const active = (members.data ?? []).filter((m) => m.status === 'active' || m.userId === form.ownerUserId);
    return [
      { value: NO_OWNER, label: 'Bez opiekuna' },
      ...active.map((m) => ({ value: m.userId, label: m.userId === session.user.id ? `${m.name} (Ty)` : m.name })),
    ];
  }, [members.data, form.ownerUserId, session.user.id]);

  const create = trpc.clients.create.useMutation();
  const update = trpc.clients.update.useMutation();
  const pending = create.isPending || update.isPending;

  function validate(): Errors {
    const e: Errors = {};
    if (!form.name.trim()) e.name = 'Podaj nazwę klienta';
    if (seesRates && form.defaultRate.trim() && !RATE_RE.test(form.defaultRate.trim())) {
      e.defaultRate = 'Podaj stawkę, np. 150 albo 150,50';
    }
    if (form.nip.trim()) {
      const nip = taxIdSchema.safeParse(form.nip);
      if (!nip.success) e.nip = nip.error.issues[0]?.message ?? 'Nieprawidłowy NIP';
    }
    if (form.regon.trim() && !isValidRegon(form.regon.trim())) e.regon = 'Nieprawidłowy REGON (9 albo 14 cyfr)';
    if (form.krs.trim() && !/^\d{10}$/.test(form.krs.replace(/\s/g, ''))) e.krs = 'Numer KRS ma 10 cyfr';
    if (form.website.trim()) {
      try {
        new URL(normalizeUrlInput(form.website));
      } catch {
        e.website = 'Nieprawidłowy adres strony';
      }
    }
    if (form.color && !isValidHex(form.color)) e.color = 'Podaj kolor w formacie #RRGGBB';
    form.links.forEach((l, i) => {
      if (!l.label.trim() && !l.url.trim()) return;
      if (!l.label.trim()) e[`link.${i}.label`] = 'Podaj nazwę';
      try {
        new URL(normalizeUrlInput(l.url));
        if (!l.url.trim()) throw new Error();
      } catch {
        e[`link.${i}.url`] = 'Nieprawidłowy adres';
      }
    });
    return e;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const address = {
      street: form.street.trim(),
      buildingNo: form.buildingNo.trim(),
      apartmentNo: form.apartmentNo.trim(),
      postalCode: form.postalCode.trim(),
      city: form.city.trim(),
      country: form.country.trim() || 'PL',
    };
    const hasAddress = Boolean(address.street || address.buildingNo || address.postalCode || address.city);
    const company: ClientCompanyInput = {
      ...(form.legalName.trim() ? { legalName: form.legalName.trim() } : {}),
      ...(form.nip.trim() ? { nip: form.nip.trim() } : {}),
      ...(form.regon.trim() ? { regon: form.regon.trim() } : {}),
      ...(form.krs.trim() ? { krs: form.krs.trim() } : {}),
      ...(form.vatStatus ? { vatStatus: form.vatStatus } : {}),
      ...(hasAddress ? { address } : {}),
      ...(form.website.trim() ? { website: normalizeUrlInput(form.website) } : {}),
      ...(form.registrySource
        ? {
            registrySource: form.registrySource,
            ...(form.registryCheckedAt ? { registryCheckedAt: form.registryCheckedAt } : {}),
          }
        : {}),
    };
    const links = form.links
      .filter((l) => l.label.trim() || l.url.trim())
      .map((l) => ({ label: l.label.trim(), url: normalizeUrlInput(l.url) }));
    const rates = seesRates
      ? {
          defaultRate: form.defaultRate.trim() ? form.defaultRate.trim().replace(',', '.') : null,
          currency: form.currency,
        }
      : {};
    const common = {
      name: form.name.trim(),
      company,
      color: form.color ? normalizeHex(form.color) : null,
      notes: form.notes.trim() ? form.notes : null,
      links,
      ownerUserId: form.ownerUserId,
      ...rates,
    };

    try {
      const saved = client
        ? await update.mutateAsync({
            id: client.id,
            version: client.version,
            ...common,
            ...(client.status === 'archived' ? {} : { status: form.status }),
          })
        : await create.mutateAsync({ ...common, status: form.status });
      await utils.clients.invalidate();
      toast.success(client ? 'Zapisano zmiany' : `Dodano klienta ${saved.name}`);
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'CONFLICT') {
        setFormError(
          'Ktoś w międzyczasie zmienił dane klienta. Zamknij okno i otwórz je ponownie, aby zobaczyć zmiany.',
        );
        return;
      }
      const issues = validationIssues(err);
      if (issues.length > 0) {
        const next: Errors = {};
        for (const issue of issues) next[fieldKey(issue.path)] ??= issue.message;
        if (COMPANY_KEYS.some((k) => next[k])) setShowCompany(true);
        setErrors(next);
        if (next.form) setFormError(next.form);
        return;
      }
      setFormError(errorMessage(err));
    }
  }

  const statusOptions: SelectOption<ClientEditableStatus>[] = CLIENT_EDITABLE_STATUSES.map((s) => ({
    value: s,
    label: CLIENT_STATUS_LABELS[s],
  }));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !pending && onOpenChange(next)}
      title={editing ? 'Edytuj dane klienta' : 'Nowy klient'}
      width="l"
      className={FULLSCREEN_ON_PHONE}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="client-form" loading={pending}>
            {editing ? 'Zapisz' : 'Dodaj klienta'}
          </Button>
        </>
      }
    >
      <form id="client-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {formError && <FormMessage>{formError}</FormMessage>}

        {!editing && (
          <NipLookupField
            value={form.nip}
            onChange={setNip}
            onUse={applyRegistryResult}
            usedLabel={form.registrySource ? registrySourceLabel(form.registrySource, form.registryCheckedAt) : null}
            error={errors.nip}
            autoFocus
          />
        )}

        <Field
          label="Nazwa"
          error={errors.name}
          hint={form.legalName && !editing ? 'Nazwa na listach — może być krótsza niż nazwa prawna' : undefined}
        >
          <Input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="np. REA Nieruchomości"
            autoFocus={editing}
            maxLength={200}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          {client?.status !== 'archived' && (
            <Field label="Status" error={errors.status}>
              <Select value={form.status} onValueChange={(v) => set('status', v)} options={statusOptions} />
            </Field>
          )}
          <Field label="Opiekun" error={errors.ownerUserId}>
            <Select
              value={form.ownerUserId ?? NO_OWNER}
              onValueChange={(v) => set('ownerUserId', v === NO_OWNER ? null : v)}
              options={ownerOptions}
              placeholder={members.isPending ? 'Wczytywanie…' : 'Wybierz osobę'}
            />
          </Field>
        </div>

        {seesRates && (
          <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
            <Field label="Stawka domyślna netto" error={errors.defaultRate} hint="Za godzinę. Puste = bez stawki.">
              <Input
                mono
                inputMode="decimal"
                value={form.defaultRate}
                onChange={(e) => set('defaultRate', e.target.value)}
                placeholder="190"
              />
            </Field>
            <Field label="Waluta" error={errors.currency}>
              <Select
                value={form.currency}
                onValueChange={(v) => set('currency', v)}
                options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              />
            </Field>
          </div>
        )}

        <ClientColorField value={form.color} onChange={(c) => set('color', c)} error={errors.color} />

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setShowCompany((v) => !v)}
            aria-expanded={showCompany}
            className="inline-flex w-fit items-center gap-1 rounded-xs text-caption font-medium text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
          >
            {showCompany ? (
              <ChevronDown size={14} strokeWidth={1.75} aria-hidden />
            ) : (
              <ChevronRight size={14} strokeWidth={1.75} aria-hidden />
            )}
            Dane firmy (opcjonalnie)
          </button>
          {showCompany && (
            <div className="grid gap-4 sm:grid-cols-6">
              <Field label="Nazwa prawna" error={errors.legalName} hint="Na fakturze" className="sm:col-span-6">
                <Input
                  value={form.legalName}
                  onChange={(e) => set('legalName', e.target.value)}
                  placeholder="np. REA NIERUCHOMOŚCI SP. Z O.O."
                  maxLength={500}
                />
              </Field>
              {editing && (
                <Field label="NIP" error={errors.nip} className="sm:col-span-2">
                  <Input mono value={form.nip} onChange={(e) => setNip(e.target.value)} maxLength={20} />
                </Field>
              )}
              <Field label="REGON" error={errors.regon} className={editing ? 'sm:col-span-2' : 'sm:col-span-3'}>
                <Input mono value={form.regon} onChange={(e) => set('regon', e.target.value)} maxLength={17} />
              </Field>
              <Field label="KRS" error={errors.krs} className={editing ? 'sm:col-span-2' : 'sm:col-span-3'}>
                <Input mono value={form.krs} onChange={(e) => set('krs', e.target.value)} maxLength={12} />
              </Field>
              <Field label="Ulica" error={errors.street} className="sm:col-span-4">
                <Input value={form.street} onChange={(e) => set('street', e.target.value)} maxLength={200} />
              </Field>
              <Field label="Nr budynku" error={errors.buildingNo} className="sm:col-span-1">
                <Input value={form.buildingNo} onChange={(e) => set('buildingNo', e.target.value)} maxLength={20} />
              </Field>
              <Field label="Nr lokalu" error={errors.apartmentNo} className="sm:col-span-1">
                <Input value={form.apartmentNo} onChange={(e) => set('apartmentNo', e.target.value)} maxLength={20} />
              </Field>
              <Field label="Kod pocztowy" error={errors.postalCode} className="sm:col-span-2">
                <Input
                  mono
                  value={form.postalCode}
                  onChange={(e) => set('postalCode', e.target.value)}
                  placeholder="00-000"
                  maxLength={20}
                />
              </Field>
              <Field label="Miejscowość" error={errors.city} className="sm:col-span-3">
                <Input value={form.city} onChange={(e) => set('city', e.target.value)} maxLength={100} />
              </Field>
              <Field label="Kraj" error={errors.country} className="sm:col-span-1">
                <Input value={form.country} onChange={(e) => set('country', e.target.value)} maxLength={60} />
              </Field>
              <Field label="Strona www" error={errors.website} className="sm:col-span-6">
                <Input
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  placeholder="rea.pl"
                  inputMode="url"
                />
              </Field>
              {(form.vatStatus || form.registrySource) && (
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-fg-muted sm:col-span-6">
                  {form.vatStatus && (
                    <Status tone={COMPANY_VAT_STATUS_TONES[form.vatStatus]}>
                      {COMPANY_VAT_STATUS_LABELS[form.vatStatus]}
                    </Status>
                  )}
                  {form.registrySource && (
                    <span>Źródło: {registrySourceLabel(form.registrySource, form.registryCheckedAt)}</span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        <Field label="Notatki" error={errors.notes}>
          <Textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Ustalenia, preferencje, ważne informacje"
          />
        </Field>

        <div className="flex flex-col gap-2">
          <Label>Odnośniki</Label>
          {form.links.length === 0 && (
            <p className="text-caption text-fg-muted">Np. dysk z plikami, menedżer haseł, Figma. Bez haseł.</p>
          )}
          {form.links.map((link, i) => (
            <div key={link.key} className="grid grid-cols-[1fr_1.6fr_auto] items-start gap-2">
              <Field error={errors[`link.${i}.label`]}>
                <Input
                  size="s"
                  aria-label={`Nazwa odnośnika ${i + 1}`}
                  placeholder="Nazwa"
                  value={link.label}
                  onChange={(e) =>
                    set(
                      'links',
                      form.links.map((l) => (l.key === link.key ? { ...l, label: e.target.value } : l)),
                    )
                  }
                />
              </Field>
              <Field error={errors[`link.${i}.url`]}>
                <Input
                  size="s"
                  aria-label={`Adres odnośnika ${i + 1}`}
                  placeholder="https://"
                  inputMode="url"
                  value={link.url}
                  onChange={(e) =>
                    set(
                      'links',
                      form.links.map((l) => (l.key === link.key ? { ...l, url: e.target.value } : l)),
                    )
                  }
                />
              </Field>
              <IconButton
                icon={X}
                label={`Usuń odnośnik ${i + 1}`}
                size={32}
                iconSize={16}
                onClick={() =>
                  set(
                    'links',
                    form.links.filter((l) => l.key !== link.key),
                  )
                }
              />
            </div>
          ))}
          {form.links.length < 50 && (
            <Button
              variant="ghost"
              size="s"
              icon={Plus}
              className="w-fit"
              onClick={() => set('links', [...form.links, { key: ++linkSeq, label: '', url: '' }])}
            >
              Dodaj odnośnik
            </Button>
          )}
        </div>
      </form>
    </Dialog>
  );
}
